(function() {
  'use strict';

  const ClubAPI = {
    baseUrl: '/apps/club-endpoint',

    /**
     * 会員詳細を取得
     * @param {string} consumerId
     * @returns {Promise<Object>}
     */
    async getCustomerDetail(consumerId) {
      const url = `${this.baseUrl}?consumer_id=${encodeURIComponent(consumerId)}&action=detail`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      return response.json();
    },

    /**
     * ポイント取引履歴を取得
     * @param {string} consumerId
     * @param {number} limit
     * @param {number} offset
     * @returns {Promise<Object>}
     */
    async getPointTransactions(consumerId, limit = 1000, offset = 0) {
      const url = `${this.baseUrl}?consumer_id=${encodeURIComponent(consumerId)}&type=point-transactions&limit=${limit}&offset=${offset}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      return response.json();
    }
  };

	// ===========================================
  // マイページ用
  // ===========================================
  const MemberCardController = {
    container: null,
    consumerId: null,

    init() {
      if (document.querySelector('[data-club-history]')) return;

      this.container = document.querySelector('.c-club-member[data-club-consumer-id]');
      if (!this.container) return;

      this.consumerId = this.container.dataset.clubConsumerId;
      if (!this.consumerId) return;

      this.load();
    },

    async load() {
      const loadingEl = this.container.querySelector('.c-club-member__loading');
      const contentEl = this.container.querySelector('.c-club-member__content');
      const errorEl = this.container.querySelector('.c-club-member__error');

      try {
        const responseData = await ClubAPI.getCustomerDetail(this.consumerId);
				const data = responseData?.data;

        if (data?.is_withdrawn === true) {
          this.container.style.display = 'none';
          return;
        }

        const rankEl = this.container.querySelector('[data-club-rank]');
        const pointsEl = this.container.querySelector('[data-club-points]');

        if (rankEl) rankEl.textContent = data?.brand_rank_tier_title || '-';
        if (pointsEl) pointsEl.textContent = this.formatNumber(data?.available_points || 0);

        loadingEl.style.display = 'none';
        contentEl.style.display = '';
      } catch (error) {
        console.error('CLUB API Error:', error);
        loadingEl.style.display = 'none';
        errorEl.style.display = '';
      }
    },

    formatNumber(num) {
      return num.toLocaleString('ja-JP');
    }
  };

  // ===========================================
  // ポイント履歴ページ
  // ===========================================
  const HistoryController = {
    container: null,
    consumerId: null,
    limit: 1000,
    offset: 0,
    hasMore: true,
    isLoading: false,

    init() {
      this.container = document.querySelector('[data-club-history]');
      if (!this.container) return;

      this.consumerId = this.container.dataset.clubConsumerId;
      if (!this.consumerId) return;

      this.load();
      this.bindEvents();
    },

    bindEvents() {
      const loadMoreBtn = this.container.querySelector('[data-club-load-more]');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', () => this.loadMore());
      }
    },

    async load() {
      const loadingEl = this.container.querySelector('.c-club-history__loading');
      const contentEl = this.container.querySelector('.c-club-history__content');
      const errorEl = this.container.querySelector('.c-club-history__error');

      try {
        const [detailDataResponse, transactionsDataResponse] = await Promise.all([
          ClubAPI.getCustomerDetail(this.consumerId),
          ClubAPI.getPointTransactions(this.consumerId, this.limit, this.offset)
        ]);

        const detailData = detailDataResponse?.data;

        if (detailData.is_withdrawn === true) {
          this.container.style.display = 'none';
          return;
        }

        this.renderSummary(detailData);

        const transactions = transactionsDataResponse?.data || [];
        this.renderTransactions(transactions);
        this.offset += transactions.length;
        this.hasMore = transactionsDataResponse?.meta?.has_more === true;
        this.updateLoadMoreButton();

        loadingEl.style.display = 'none';
        contentEl.style.display = '';
      } catch (error) {
        console.error('CLUB API Error:', error);
        loadingEl.style.display = 'none';
        errorEl.style.display = '';
      }
    },

    async loadMore() {
      if (this.isLoading || !this.hasMore) return;

      this.isLoading = true;
      const btn = this.container.querySelector('[data-club-load-more]');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<span class="c-club-loader"></span> 読み込み中...';
      }

      try {
        const responseData = await ClubAPI.getPointTransactions(this.consumerId, this.limit, this.offset);
        const transactions = responseData?.data || [];
        this.renderTransactions(transactions, true);
        this.offset += transactions.length;
        this.hasMore = responseData?.meta?.has_more === true;
      } catch (error) {
        console.error('CLUB API Error:', error);
      } finally {
        this.isLoading = false;
        this.updateLoadMoreButton();
      }
    },

    renderSummary(data) {
      const rankEl = this.container.querySelector('[data-club-summary-rank]');
      const availableEl = this.container.querySelector('[data-club-summary-available]');
      const totalEl = this.container.querySelector('[data-club-summary-total]');

      if (rankEl) rankEl.textContent = data.brand_rank_tier_title || '-';
      if (availableEl) availableEl.textContent = this.formatNumber(data.available_points || 0);
      if (totalEl) totalEl.textContent = this.formatNumber(data.total_earned_points || 0);
    },

    renderTransactions(transactions, append = false) {
      const tbody = this.container.querySelector('[data-club-transactions-body]');
      const emptyEl = this.container.querySelector('.c-club-transactions__empty');

      if (!tbody) return;

      if (!append) {
        tbody.innerHTML = '';
      }

      if (transactions.length === 0 && !append) {
        if (emptyEl) emptyEl.style.display = '';
        return;
      }

      if (emptyEl) emptyEl.style.display = 'none';

      transactions.forEach(tx => {
        const row = document.createElement('tr');
        const amount = Math.abs(tx.amount || 0);
        const isEarn = amount >= 0;
        const pointsClass = isEarn ? 'c-club-transactions__points--plus' : 'c-club-transactions__points--minus';
        const pointsPrefix = isEarn ? '+' : '-';

        row.innerHTML = `
          <td class="c-club-transactions__date">${this.formatDate(tx.created_at)}</td>
          <td>${this.escapeHtml(tx.reason_text || '-')}</td>
          <td class="c-club-transactions__points ${pointsClass}">${pointsPrefix}${this.formatNumber(amount)} ボニー</td>
        `;
        tbody.appendChild(row);
      });
    },

    updateLoadMoreButton() {
      const btn = this.container.querySelector('[data-club-load-more]');
      const wrapper = this.container.querySelector('.c-club-load-more');

      if (!btn || !wrapper) return;

      if (!this.hasMore) {
        wrapper.style.display = 'none';
      } else {
        wrapper.style.display = '';
        btn.disabled = false;
        btn.textContent = 'さらに読み込む';
      }
    },

    formatNumber(num) {
      return num.toLocaleString('ja-JP');
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}/${m}/${d}`;
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    MemberCardController.init();
    HistoryController.init();
  });
})();
